// src/features/attachment/AttachmentDetailRail.tsx
// Right-side panel for a single attachment inside the Attachment Manager.
// Mirrors the calendar event rail pattern: clicking a row opens this panel
// without losing the grid behind it.

import { useEffect, useState, useCallback } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button, Badge } from '@react/components';
import { attachmentApi } from '@core/api';
import type { Attachment, GetProcessingStatusResponse, OwnershipRecord } from '@core/types/attachment';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { UserPicker } from '@/components/UserPicker';
import {
  formatFileSize,
  getFileExtension,
  isImageMimeType,
  processingStatusVariant,
} from './logic';
import { fmtDateTime, errMessage } from './format';

type Tab = 'overview' | 'processing' | 'ownership';

export interface AttachmentDetailRailProps {
  attachmentId: string;
  onClose: () => void;
  onDeleted: (id: string) => void;
  onUpdated: (attachment: Attachment) => void;
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 18 18 6M6 6l12 12" />
    </svg>
  );
}

export const AttachmentDetailRail = observer(({ attachmentId: id, onClose, onDeleted, onUpdated }: AttachmentDetailRailProps) => {
  const { t, locale } = useI18n();
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');

  // Rename / description
  const [isEditing, setIsEditing] = useState(false);
  const [fileName, setFileName] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Delete
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Processing
  const [processing, setProcessing] = useState<GetProcessingStatusResponse | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Ownership
  const [history, setHistory] = useState<OwnershipRecord[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [newOwnerId, setNewOwnerId] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);

  const load = useCallback(() => {
    setIsLoading(true);
    setError(null);
    attachmentApi.GetAttachment(id, { params: { include_download_url: true } })
      .then((res) => {
        setAttachment(res.attachment ?? null);
        setFileName(res.attachment?.file_name ?? '');
        setDescription(res.attachment?.metadata?.description ?? '');
      })
      .catch((e) => setError(errMessage(e, t('attachment.manager.loadError'))))
      .finally(() => setIsLoading(false));
  }, [id, t]);

  useEffect(() => { load(); }, [load]);

  // Poll processing status while active, and when the Processing tab is open.
  useEffect(() => {
    if (tab !== 'processing') return;
    let cancelled = false;
    const poll = () => {
      attachmentApi.GetProcessingStatus(id)
        .then((res) => { if (!cancelled) setProcessing(res); })
        .catch(() => {});
    };
    poll();
    const isActive = processing?.status === 'ATTACHMENT_PROCESSING_STATUS_PROCESSING'
      || processing?.status === 'ATTACHMENT_PROCESSING_STATUS_PENDING';
    const interval = isActive ? window.setInterval(poll, 3000) : undefined;
    return () => { cancelled = true; if (interval) window.clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, id, processing?.status]);

  useEffect(() => {
    if (tab !== 'ownership') return;
    setIsLoadingHistory(true);
    attachmentApi.GetOwnershipHistory(id)
      .then((res) => setHistory(res.records?.records ?? []))
      .catch(() => setHistory([]))
      .finally(() => setIsLoadingHistory(false));
  }, [tab, id]);

  const handleSaveEdit = async () => {
    if (!attachment) return;
    setIsSaving(true);
    try {
      const res = await attachmentApi.UpdateAttachment(id, {
        attachment: {
          ...attachment,
          file_name: fileName,
          metadata: { ...(attachment.metadata ?? {}), description },
        },
      });
      if (res.attachment) {
        setAttachment(res.attachment);
        onUpdated(res.attachment);
      }
      setIsEditing(false);
    } catch (e) {
      setError(errMessage(e, t('attachment.manager.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await attachmentApi.DeleteAttachment(id);
      onDeleted(id);
      onClose();
    } catch (e) {
      setError(errMessage(e, t('attachment.manager.deleteError')));
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleDownload = async () => {
    try {
      const res = await attachmentApi.GetDownloadUrl(id, { params: { inline: false } });
      const url = res.download_url?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(errMessage(e, t('attachment.manager.downloadError')));
    }
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await attachmentApi.RetryProcessing(id, { task_names: [] });
      const res = await attachmentApi.GetProcessingStatus(id);
      setProcessing(res);
    } catch (e) {
      setError(errMessage(e, t('attachment.manager.processing.retryError')));
    } finally {
      setIsRetrying(false);
    }
  };

  const handleTransfer = async () => {
    if (!newOwnerId.trim()) return;
    setIsTransferring(true);
    try {
      const res = await attachmentApi.TransferOwnership(id, {
        new_owner: { id: newOwnerId, type_id: 'user' } as any,
        transfer_reason: transferReason,
      });
      if (res.attachment) {
        setAttachment(res.attachment);
        onUpdated(res.attachment);
      }
      setTransferOpen(false);
      setNewOwnerId('');
      setTransferReason('');
      if (tab === 'ownership') {
        const historyRes = await attachmentApi.GetOwnershipHistory(id);
        setHistory(historyRes.records?.records ?? []);
      }
    } catch (e) {
      setError(errMessage(e, t('attachment.manager.ownership.transferError')));
    } finally {
      setIsTransferring(false);
    }
  };

  const isImage = attachment ? isImageMimeType(attachment.mime_type) : false;

  return (
    <aside
      aria-label={t('attachment.manager.rail.label')}
      className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-border bg-background shadow-xl xl:relative xl:inset-auto xl:shadow-none"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="truncate text-sm font-semibold text-foreground">
          {attachment?.file_name ?? t('attachment.manager.rail.label')}
        </h2>
        <button
          onClick={onClose}
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label={t('common.close')}
        >
          <CloseIcon className="h-4 w-4" />
        </button>
      </div>

      {isLoading && (
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          {t('common.loading')}
        </div>
      )}

      {!isLoading && attachment && (
        <>
          <div className="flex border-b border-border px-4">
            {(['overview', 'processing', 'ownership'] as Tab[]).map((tb) => (
              <button
                key={tb}
                onClick={() => setTab(tb)}
                className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                  tab === tb ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {t(`attachment.manager.rail.tab.${tb}`)}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            {tab === 'overview' && (
              <div className="space-y-4">
                {isImage && attachment.download_url?.url ? (
                  <img
                    src={attachment.download_url.url}
                    alt={attachment.file_name}
                    className="w-full rounded-md border border-border object-contain max-h-64 bg-muted"
                  />
                ) : (
                  <div className="flex h-24 w-full items-center justify-center rounded-md border border-border bg-muted text-sm font-bold text-muted-foreground">
                    {getFileExtension(attachment.file_name) || 'FILE'}
                  </div>
                )}

                {!isEditing ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-foreground break-all">{attachment.file_name}</p>
                      <Badge variant={processingStatusVariant(attachment.processing_status)} size="sm">
                        {t(`attachment.manager.status.${attachment.processing_status ?? 'ATTACHMENT_PROCESSING_STATUS_UNSPECIFIED'}`)}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {attachment.mime_type} · {formatFileSize(attachment.size_bytes)}
                    </p>
                    {attachment.metadata?.description && (
                      <p className="text-sm text-foreground">{attachment.metadata.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {t('attachment.manager.rail.createdAt', { date: fmtDateTime(attachment.created_at, locale) })}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-muted-foreground" htmlFor="att-rename">
                      {t('attachment.manager.rail.fileName')}
                    </label>
                    <input
                      id="att-rename"
                      value={fileName}
                      onChange={(e) => setFileName(e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <label className="block text-xs font-medium text-muted-foreground" htmlFor="att-desc">
                      {t('attachment.manager.rail.description')}
                    </label>
                    <textarea
                      id="att-desc"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => { setIsEditing(false); setFileName(attachment.file_name); setDescription(attachment.metadata?.description ?? ''); }}
                        className="rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted"
                      >
                        {t('common.cancel')}
                      </button>
                      <Button size="sm" onClick={handleSaveEdit} isLoading={isSaving}>{t('common.save')}</Button>
                    </div>
                  </div>
                )}

                {!isEditing && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button size="sm" variant="secondary" onClick={handleDownload}>
                      {t('attachment.manager.rail.download')}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setIsEditing(true)}>
                      {t('common.edit')}
                    </Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirmDelete(true)}>
                      {t('common.delete')}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {tab === 'processing' && (
              <div className="space-y-3">
                {!processing ? (
                  <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <Badge variant={processingStatusVariant(processing.status)}>
                        {t(`attachment.manager.status.${processing.status ?? 'ATTACHMENT_PROCESSING_STATUS_UNSPECIFIED'}`)}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{processing.progress_percent}%</span>
                    </div>
                    {processing.error && (
                      <p className="text-sm text-destructive">{processing.error}</p>
                    )}
                    <ul className="space-y-1.5">
                      {(processing.tasks?.tasks ?? []).map((task) => (
                        <li key={task.name} className="flex items-center justify-between rounded-md border border-border px-2.5 py-1.5 text-sm">
                          <span className="text-foreground">{task.name}</span>
                          <Badge variant={processingStatusVariant(task.status)} size="sm">
                            {t(`attachment.manager.status.${task.status ?? 'ATTACHMENT_PROCESSING_STATUS_UNSPECIFIED'}`)}
                          </Badge>
                        </li>
                      ))}
                      {(processing.tasks?.tasks ?? []).length === 0 && (
                        <p className="text-sm text-muted-foreground">{t('attachment.manager.processing.noTasks')}</p>
                      )}
                    </ul>
                    {processing.status === 'ATTACHMENT_PROCESSING_STATUS_FAILED' && (
                      <Button size="sm" variant="secondary" onClick={handleRetry} isLoading={isRetrying}>
                        {t('attachment.manager.processing.retry')}
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}

            {tab === 'ownership' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-foreground">
                    {t('attachment.manager.ownership.current')}: <span className="font-medium">{attachment.owner?.display_name || attachment.owner?.entity?.id || '—'}</span>
                  </p>
                  <Button size="sm" variant="secondary" onClick={() => setTransferOpen((o) => !o)}>
                    {t('attachment.manager.ownership.transfer')}
                  </Button>
                </div>

                {transferOpen && (
                  <div className="space-y-2 rounded-md border border-border p-3">
                    <UserPicker
                      value={newOwnerId}
                      onChange={(userId) => setNewOwnerId(userId)}
                      label={t('attachment.manager.ownership.newOwner')}
                      placeholder={t('attachment.manager.ownership.newOwnerPlaceholder')}
                    />
                    <input
                      value={transferReason}
                      onChange={(e) => setTransferReason(e.target.value)}
                      placeholder={t('attachment.manager.ownership.reasonPlaceholder')}
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setTransferOpen(false)}
                        className="rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted"
                      >
                        {t('common.cancel')}
                      </button>
                      <Button size="sm" onClick={handleTransfer} isLoading={isTransferring} disabled={!newOwnerId.trim()}>
                        {t('attachment.manager.ownership.transfer')}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="space-y-1.5">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('attachment.manager.ownership.history')}
                  </p>
                  {isLoadingHistory && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
                  {!isLoadingHistory && history.length === 0 && (
                    <p className="text-sm text-muted-foreground">{t('attachment.manager.ownership.noHistory')}</p>
                  )}
                  {!isLoadingHistory && history.map((record, idx) => (
                    <div key={idx} className="rounded-md border border-border px-2.5 py-2 text-sm">
                      <p className="text-foreground">
                        {record.previous_owner?.id ?? '—'} → {record.new_owner?.id ?? '—'}
                      </p>
                      {record.transfer_reason && (
                        <p className="text-xs text-muted-foreground">{record.transfer_reason}</p>
                      )}
                      <p className="text-xs text-muted-foreground">{fmtDateTime(record.transferred_at, locale)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={t('attachment.deleteConfirm')}
        destructive
        isConfirming={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </aside>
  );
});
